/**
 * @license
 * Copyright 2025 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

// Code generated by the Google Gen AI SDK generator DO NOT EDIT.

import * as common from '../_common.js';
import * as t from '../_transformers.js';
import type * as types from '../types.js';

export function cancelTuningJobParametersToMldev(
  fromObject: types.CancelTuningJobParameters,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['_url', 'name'], fromName);
  }

  return toObject;
}

export function cancelTuningJobParametersToVertex(
  fromObject: types.CancelTuningJobParameters,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['_url', 'name'], fromName);
  }

  return toObject;
}

export function cancelTuningJobResponseFromMldev(
  fromObject: types.CancelTuningJobResponse,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  return toObject;
}

export function cancelTuningJobResponseFromVertex(
  fromObject: types.CancelTuningJobResponse,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  return toObject;
}

export function codeExecutionResultToVertex(
  fromObject: types.CodeExecutionResult,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromOutcome = common.getValueByPath(fromObject, ['outcome']);
  if (fromOutcome != null) {
    common.setValueByPath(toObject, ['outcome'], fromOutcome);
  }

  const fromOutput = common.getValueByPath(fromObject, ['output']);
  if (fromOutput != null) {
    common.setValueByPath(toObject, ['output'], fromOutput);
  }

  if (common.getValueByPath(fromObject, ['id']) !== undefined) {
    throw new Error(
      'id parameter is only supported in Gemini Developer API mode, not in Gemini Enterprise Agent Platform mode.',
    );
  }

  return toObject;
}

export function contentToVertex(
  fromObject: types.Content,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromParts = common.getValueByPath(fromObject, ['parts']);
  if (fromParts != null) {
    let transformedList = fromParts;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return partToVertex(item, rootObject);
      });
    }
    common.setValueByPath(toObject, ['parts'], transformedList);
  }

  const fromRole = common.getValueByPath(fromObject, ['role']);
  if (fromRole != null) {
    common.setValueByPath(toObject, ['role'], fromRole);
  }

  return toObject;
}

export function createTuningJobConfigToMldev(
  fromObject: types.CreateTuningJobConfig,
  parentObject: Record<string, unknown>,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  if (common.getValueByPath(fromObject, ['validationDataset']) !== undefined) {
    throw new Error(
      'validationDataset parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromTunedModelDisplayName = common.getValueByPath(fromObject, [
    'tunedModelDisplayName',
  ]);
  if (parentObject !== undefined && fromTunedModelDisplayName != null) {
    common.setValueByPath(
      parentObject,
      ['displayName'],
      fromTunedModelDisplayName,
    );
  }

  if (common.getValueByPath(fromObject, ['description']) !== undefined) {
    throw new Error(
      'description parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromEpochCount = common.getValueByPath(fromObject, ['epochCount']);
  if (parentObject !== undefined && fromEpochCount != null) {
    common.setValueByPath(
      parentObject,
      ['tuningTask', 'hyperparameters', 'epochCount'],
      fromEpochCount,
    );
  }

  const fromLearningRateMultiplier = common.getValueByPath(fromObject, [
    'learningRateMultiplier',
  ]);
  if (fromLearningRateMultiplier != null) {
    common.setValueByPath(
      toObject,
      ['tuningTask', 'hyperparameters', 'learningRateMultiplier'],
      fromLearningRateMultiplier,
    );
  }

  if (
    common.getValueByPath(fromObject, ['exportLastCheckpointOnly']) !==
    undefined
  ) {
    throw new Error(
      'exportLastCheckpointOnly parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (
    common.getValueByPath(fromObject, ['preTunedModelCheckpointId']) !==
    undefined
  ) {
    throw new Error(
      'preTunedModelCheckpointId parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['adapterSize']) !== undefined) {
    throw new Error(
      'adapterSize parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['tuningMode']) !== undefined) {
    throw new Error(
      'tuningMode parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['customBaseModel']) !== undefined) {
    throw new Error(
      'customBaseModel parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromBatchSize = common.getValueByPath(fromObject, ['batchSize']);
  if (parentObject !== undefined && fromBatchSize != null) {
    common.setValueByPath(
      parentObject,
      ['tuningTask', 'hyperparameters', 'batchSize'],
      fromBatchSize,
    );
  }

  const fromLearningRate = common.getValueByPath(fromObject, ['learningRate']);
  if (parentObject !== undefined && fromLearningRate != null) {
    common.setValueByPath(
      parentObject,
      ['tuningTask', 'hyperparameters', 'learningRate'],
      fromLearningRate,
    );
  }

  if (common.getValueByPath(fromObject, ['labels']) !== undefined) {
    throw new Error(
      'labels parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['beta']) !== undefined) {
    throw new Error(
      'beta parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['baseTeacherModel']) !== undefined) {
    throw new Error(
      'baseTeacherModel parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (
    common.getValueByPath(fromObject, ['tunedTeacherModelSource']) !== undefined
  ) {
    throw new Error(
      'tunedTeacherModelSource parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (
    common.getValueByPath(fromObject, ['sftLossWeightMultiplier']) !== undefined
  ) {
    throw new Error(
      'sftLossWeightMultiplier parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['outputUri']) !== undefined) {
    throw new Error(
      'outputUri parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['rewardConfig']) !== undefined) {
    throw new Error(
      'rewardConfig parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (
    common.getValueByPath(fromObject, ['compositeRewardConfig']) !== undefined
  ) {
    throw new Error(
      'compositeRewardConfig parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['samplesPerPrompt']) !== undefined) {
    throw new Error(
      'samplesPerPrompt parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['evaluateInterval']) !== undefined) {
    throw new Error(
      'evaluateInterval parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['checkpointInterval']) !== undefined) {
    throw new Error(
      'checkpointInterval parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['maxOutputTokens']) !== undefined) {
    throw new Error(
      'maxOutputTokens parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['thinkingLevel']) !== undefined) {
    throw new Error(
      'thinkingLevel parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (
    common.getValueByPath(fromObject, ['validationDatasetUri']) !== undefined
  ) {
    throw new Error(
      'validationDatasetUri parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['encryptionSpec']) !== undefined) {
    throw new Error(
      'encryptionSpec parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  return toObject;
}

export function createTuningJobConfigToVertex(
  fromObject: types.CreateTuningJobConfig,
  parentObject: Record<string, unknown>,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  let discriminatorValidationDataset = common.getValueByPath(rootObject, [
    'config',
    'method',
  ]);
  if (discriminatorValidationDataset === undefined) {
    discriminatorValidationDataset = 'SUPERVISED_FINE_TUNING';
  }
  if (discriminatorValidationDataset === 'SUPERVISED_FINE_TUNING') {
    const fromValidationDataset = common.getValueByPath(fromObject, [
      'validationDataset',
    ]);
    if (parentObject !== undefined && fromValidationDataset != null) {
      common.setValueByPath(
        parentObject,
        ['supervisedTuningSpec'],
        tuningValidationDatasetToVertex(fromValidationDataset, rootObject),
      );
    }
  } else if (discriminatorValidationDataset === 'PREFERENCE_TUNING') {
    const fromValidationDataset = common.getValueByPath(fromObject, [
      'validationDataset',
    ]);
    if (parentObject !== undefined && fromValidationDataset != null) {
      common.setValueByPath(
        parentObject,
        ['preferenceOptimizationSpec'],
        tuningValidationDatasetToVertex(fromValidationDataset, rootObject),
      );
    }
  } else if (discriminatorValidationDataset === 'DISTILLATION') {
    const fromValidationDataset = common.getValueByPath(fromObject, [
      'validationDataset',
    ]);
    if (parentObject !== undefined && fromValidationDataset != null) {
      common.setValueByPath(
        parentObject,
        ['distillationSpec'],
        tuningValidationDatasetToVertex(fromValidationDataset, rootObject),
      );
    }
  } else if (discriminatorValidationDataset === 'REINFORCEMENT_TUNING') {
    const fromValidationDataset = common.getValueByPath(fromObject, [
      'validationDataset',
    ]);
    if (parentObject !== undefined && fromValidationDataset != null) {
      common.setValueByPath(
        parentObject,
        ['reinforcementTuningSpec'],
        tuningValidationDatasetToVertex(fromValidationDataset, rootObject),
      );
    }
  }

  const fromTunedModelDisplayName = common.getValueByPath(fromObject, [
    'tunedModelDisplayName',
  ]);
  if (parentObject !== undefined && fromTunedModelDisplayName != null) {
    common.setValueByPath(
      parentObject,
      ['tunedModelDisplayName'],
      fromTunedModelDisplayName,
    );
  }

  const fromDescription = common.getValueByPath(fromObject, ['description']);
  if (parentObject !== undefined && fromDescription != null) {
    common.setValueByPath(parentObject, ['description'], fromDescription);
  }

  let discriminatorEpochCount = common.getValueByPath(rootObject, [
    'config',
    'method',
  ]);
  if (discriminatorEpochCount === undefined) {
    discriminatorEpochCount = 'SUPERVISED_FINE_TUNING';
  }
  if (discriminatorEpochCount === 'SUPERVISED_FINE_TUNING') {
    const fromEpochCount = common.getValueByPath(fromObject, ['epochCount']);
    if (parentObject !== undefined && fromEpochCount != null) {
      common.setValueByPath(
        parentObject,
        ['supervisedTuningSpec', 'hyperParameters', 'epochCount'],
        fromEpochCount,
      );
    }
  } else if (discriminatorEpochCount === 'PREFERENCE_TUNING') {
    const fromEpochCount = common.getValueByPath(fromObject, ['epochCount']);
    if (parentObject !== undefined && fromEpochCount != null) {
      common.setValueByPath(
        parentObject,
        ['preferenceOptimizationSpec', 'hyperParameters', 'epochCount'],
        fromEpochCount,
      );
    }
  } else if (discriminatorEpochCount === 'DISTILLATION') {
    const fromEpochCount = common.getValueByPath(fromObject, ['epochCount']);
    if (parentObject !== undefined && fromEpochCount != null) {
      common.setValueByPath(
        parentObject,
        ['distillationSpec', 'hyperParameters', 'epochCount'],
        fromEpochCount,
      );
    }
  } else if (discriminatorEpochCount === 'REINFORCEMENT_TUNING') {
    const fromEpochCount = common.getValueByPath(fromObject, ['epochCount']);
    if (parentObject !== undefined && fromEpochCount != null) {
      common.setValueByPath(
        parentObject,
        ['reinforcementTuningSpec', 'hyperParameters', 'epochCount'],
        fromEpochCount,
      );
    }
  }

  let discriminatorLearningRateMultiplier = common.getValueByPath(rootObject, [
    'config',
    'method',
  ]);
  if (discriminatorLearningRateMultiplier === undefined) {
    discriminatorLearningRateMultiplier = 'SUPERVISED_FINE_TUNING';
  }
  if (discriminatorLearningRateMultiplier === 'SUPERVISED_FINE_TUNING') {
    const fromLearningRateMultiplier = common.getValueByPath(fromObject, [
      'learningRateMultiplier',
    ]);
    if (parentObject !== undefined && fromLearningRateMultiplier != null) {
      common.setValueByPath(
        parentObject,
        ['supervisedTuningSpec', 'hyperParameters', 'learningRateMultiplier'],
        fromLearningRateMultiplier,
      );
    }
  } else if (discriminatorLearningRateMultiplier === 'PREFERENCE_TUNING') {
    const fromLearningRateMultiplier = common.getValueByPath(fromObject, [
      'learningRateMultiplier',
    ]);
    if (parentObject !== undefined && fromLearningRateMultiplier != null) {
      common.setValueByPath(
        parentObject,
        [
          'preferenceOptimizationSpec',
          'hyperParameters',
          'learningRateMultiplier',
        ],
        fromLearningRateMultiplier,
      );
    }
  } else if (discriminatorLearningRateMultiplier === 'DISTILLATION') {
    const fromLearningRateMultiplier = common.getValueByPath(fromObject, [
      'learningRateMultiplier',
    ]);
    if (parentObject !== undefined && fromLearningRateMultiplier != null) {
      common.setValueByPath(
        parentObject,
        ['distillationSpec', 'hyperParameters', 'learningRateMultiplier'],
        fromLearningRateMultiplier,
      );
    }
  } else if (discriminatorLearningRateMultiplier === 'REINFORCEMENT_TUNING') {
    const fromLearningRateMultiplier = common.getValueByPath(fromObject, [
      'learningRateMultiplier',
    ]);
    if (parentObject !== undefined && fromLearningRateMultiplier != null) {
      common.setValueByPath(
        parentObject,
        [
          'reinforcementTuningSpec',
          'hyperParameters',
          'learningRateMultiplier',
        ],
        fromLearningRateMultiplier,
      );
    }
  }

  let discriminatorExportLastCheckpointOnly = common.getValueByPath(
    rootObject,
    ['config', 'method'],
  );
  if (discriminatorExportLastCheckpointOnly === undefined) {
    discriminatorExportLastCheckpointOnly = 'SUPERVISED_FINE_TUNING';
  }
  if (discriminatorExportLastCheckpointOnly === 'SUPERVISED_FINE_TUNING') {
    const fromExportLastCheckpointOnly = common.getValueByPath(fromObject, [
      'exportLastCheckpointOnly',
    ]);
    if (parentObject !== undefined && fromExportLastCheckpointOnly != null) {
      common.setValueByPath(
        parentObject,
        ['supervisedTuningSpec', 'exportLastCheckpointOnly'],
        fromExportLastCheckpointOnly,
      );
    }
  } else if (discriminatorExportLastCheckpointOnly === 'PREFERENCE_TUNING') {
    const fromExportLastCheckpointOnly = common.getValueByPath(fromObject, [
      'exportLastCheckpointOnly',
    ]);
    if (parentObject !== undefined && fromExportLastCheckpointOnly != null) {
      common.setValueByPath(
        parentObject,
        ['preferenceOptimizationSpec', 'exportLastCheckpointOnly'],
        fromExportLastCheckpointOnly,
      );
    }
  } else if (discriminatorExportLastCheckpointOnly === 'DISTILLATION') {
    const fromExportLastCheckpointOnly = common.getValueByPath(fromObject, [
      'exportLastCheckpointOnly',
    ]);
    if (parentObject !== undefined && fromExportLastCheckpointOnly != null) {
      common.setValueByPath(
        parentObject,
        ['distillationSpec', 'exportLastCheckpointOnly'],
        fromExportLastCheckpointOnly,
      );
    }
  }

  let discriminatorAdapterSize = common.getValueByPath(rootObject, [
    'config',
    'method',
  ]);
  if (discriminatorAdapterSize === undefined) {
    discriminatorAdapterSize = 'SUPERVISED_FINE_TUNING';
  }
  if (discriminatorAdapterSize === 'SUPERVISED_FINE_TUNING') {
    const fromAdapterSize = common.getValueByPath(fromObject, ['adapterSize']);
    if (parentObject !== undefined && fromAdapterSize != null) {
      common.setValueByPath(
        parentObject,
        ['supervisedTuningSpec', 'hyperParameters', 'adapterSize'],
        fromAdapterSize,
      );
    }
  } else if (discriminatorAdapterSize === 'PREFERENCE_TUNING') {
    const fromAdapterSize = common.getValueByPath(fromObject, ['adapterSize']);
    if (parentObject !== undefined && fromAdapterSize != null) {
      common.setValueByPath(
        parentObject,
        ['preferenceOptimizationSpec', 'hyperParameters', 'adapterSize'],
        fromAdapterSize,
      );
    }
  } else if (discriminatorAdapterSize === 'DISTILLATION') {
    const fromAdapterSize = common.getValueByPath(fromObject, ['adapterSize']);
    if (parentObject !== undefined && fromAdapterSize != null) {
      common.setValueByPath(
        parentObject,
        ['distillationSpec', 'hyperParameters', 'adapterSize'],
        fromAdapterSize,
      );
    }
  } else if (discriminatorAdapterSize === 'REINFORCEMENT_TUNING') {
    const fromAdapterSize = common.getValueByPath(fromObject, ['adapterSize']);
    if (parentObject !== undefined && fromAdapterSize != null) {
      common.setValueByPath(
        parentObject,
        ['reinforcementTuningSpec', 'hyperParameters', 'adapterSize'],
        fromAdapterSize,
      );
    }
  }

  let discriminatorTuningMode = common.getValueByPath(rootObject, [
    'config',
    'method',
  ]);
  if (discriminatorTuningMode === undefined) {
    discriminatorTuningMode = 'SUPERVISED_FINE_TUNING';
  }
  if (discriminatorTuningMode === 'SUPERVISED_FINE_TUNING') {
    const fromTuningMode = common.getValueByPath(fromObject, ['tuningMode']);
    if (parentObject !== undefined && fromTuningMode != null) {
      common.setValueByPath(
        parentObject,
        ['supervisedTuningSpec', 'tuningMode'],
        fromTuningMode,
      );
    }
  } else if (discriminatorTuningMode === 'DISTILLATION') {
    const fromTuningMode = common.getValueByPath(fromObject, ['tuningMode']);
    if (parentObject !== undefined && fromTuningMode != null) {
      common.setValueByPath(
        parentObject,
        ['distillationSpec', 'tuningMode'],
        fromTuningMode,
      );
    }
  }

  const fromCustomBaseModel = common.getValueByPath(fromObject, [
    'customBaseModel',
  ]);
  if (parentObject !== undefined && fromCustomBaseModel != null) {
    common.setValueByPath(
      parentObject,
      ['customBaseModel'],
      fromCustomBaseModel,
    );
  }

  let discriminatorBatchSize = common.getValueByPath(rootObject, [
    'config',
    'method',
  ]);
  if (discriminatorBatchSize === undefined) {
    discriminatorBatchSize = 'SUPERVISED_FINE_TUNING';
  }
  if (discriminatorBatchSize === 'SUPERVISED_FINE_TUNING') {
    const fromBatchSize = common.getValueByPath(fromObject, ['batchSize']);
    if (parentObject !== undefined && fromBatchSize != null) {
      common.setValueByPath(
        parentObject,
        ['supervisedTuningSpec', 'hyperParameters', 'batchSize'],
        fromBatchSize,
      );
    }
  } else if (discriminatorBatchSize === 'DISTILLATION') {
    const fromBatchSize = common.getValueByPath(fromObject, ['batchSize']);
    if (parentObject !== undefined && fromBatchSize != null) {
      common.setValueByPath(
        parentObject,
        ['distillationSpec', 'hyperParameters', 'batchSize'],
        fromBatchSize,
      );
    }
  } else if (discriminatorBatchSize === 'REINFORCEMENT_TUNING') {
    const fromBatchSize = common.getValueByPath(fromObject, ['batchSize']);
    if (parentObject !== undefined && fromBatchSize != null) {
      common.setValueByPath(
        parentObject,
        ['reinforcementTuningSpec', 'hyperParameters', 'batchSize'],
        fromBatchSize,
      );
    }
  }

  let discriminatorLearningRate = common.getValueByPath(rootObject, [
    'config',
    'method',
  ]);
  if (discriminatorLearningRate === undefined) {
    discriminatorLearningRate = 'SUPERVISED_FINE_TUNING';
  }
  if (discriminatorLearningRate === 'SUPERVISED_FINE_TUNING') {
    const fromLearningRate = common.getValueByPath(fromObject, [
      'learningRate',
    ]);
    if (parentObject !== undefined && fromLearningRate != null) {
      common.setValueByPath(
        parentObject,
        ['supervisedTuningSpec', 'hyperParameters', 'learningRate'],
        fromLearningRate,
      );
    }
  } else if (discriminatorLearningRate === 'DISTILLATION') {
    const fromLearningRate = common.getValueByPath(fromObject, [
      'learningRate',
    ]);
    if (parentObject !== undefined && fromLearningRate != null) {
      common.setValueByPath(
        parentObject,
        ['distillationSpec', 'hyperParameters', 'learningRate'],
        fromLearningRate,
      );
    }
  }

  const fromLabels = common.getValueByPath(fromObject, ['labels']);
  if (parentObject !== undefined && fromLabels != null) {
    common.setValueByPath(parentObject, ['labels'], fromLabels);
  }

  const fromBeta = common.getValueByPath(fromObject, ['beta']);
  if (parentObject !== undefined && fromBeta != null) {
    common.setValueByPath(
      parentObject,
      ['preferenceOptimizationSpec', 'hyperParameters', 'beta'],
      fromBeta,
    );
  }

  const fromBaseTeacherModel = common.getValueByPath(fromObject, [
    'baseTeacherModel',
  ]);
  if (parentObject !== undefined && fromBaseTeacherModel != null) {
    common.setValueByPath(
      parentObject,
      ['distillationSpec', 'baseTeacherModel'],
      fromBaseTeacherModel,
    );
  }

  const fromTunedTeacherModelSource = common.getValueByPath(fromObject, [
    'tunedTeacherModelSource',
  ]);
  if (parentObject !== undefined && fromTunedTeacherModelSource != null) {
    common.setValueByPath(
      parentObject,
      ['distillationSpec', 'tunedTeacherModelSource'],
      fromTunedTeacherModelSource,
    );
  }

  const fromSftLossWeightMultiplier = common.getValueByPath(fromObject, [
    'sftLossWeightMultiplier',
  ]);
  if (parentObject !== undefined && fromSftLossWeightMultiplier != null) {
    common.setValueByPath(
      parentObject,
      ['distillationSpec', 'hyperParameters', 'sftLossWeightMultiplier'],
      fromSftLossWeightMultiplier,
    );
  }

  const fromOutputUri = common.getValueByPath(fromObject, ['outputUri']);
  if (parentObject !== undefined && fromOutputUri != null) {
    common.setValueByPath(parentObject, ['outputUri'], fromOutputUri);
  }

  const fromRewardConfig = common.getValueByPath(fromObject, ['rewardConfig']);
  if (parentObject !== undefined && fromRewardConfig != null) {
    common.setValueByPath(
      parentObject,
      ['reinforcementTuningSpec', 'singleRewardConfig'],
      fromRewardConfig,
    );
  }

  const fromCompositeRewardConfig = common.getValueByPath(fromObject, [
    'compositeRewardConfig',
  ]);
  if (parentObject !== undefined && fromCompositeRewardConfig != null) {
    common.setValueByPath(
      parentObject,
      ['reinforcementTuningSpec', 'compositeRewardConfig'],
      fromCompositeRewardConfig,
    );
  }

  const fromSamplesPerPrompt = common.getValueByPath(fromObject, [
    'samplesPerPrompt',
  ]);
  if (parentObject !== undefined && fromSamplesPerPrompt != null) {
    common.setValueByPath(
      parentObject,
      ['reinforcementTuningSpec', 'hyperParameters', 'samplesPerPrompt'],
      fromSamplesPerPrompt,
    );
  }

  const fromEvaluateInterval = common.getValueByPath(fromObject, [
    'evaluateInterval',
  ]);
  if (parentObject !== undefined && fromEvaluateInterval != null) {
    common.setValueByPath(
      parentObject,
      ['reinforcementTuningSpec', 'hyperParameters', 'evaluateInterval'],
      fromEvaluateInterval,
    );
  }

  const fromCheckpointInterval = common.getValueByPath(fromObject, [
    'checkpointInterval',
  ]);
  if (parentObject !== undefined && fromCheckpointInterval != null) {
    common.setValueByPath(
      parentObject,
      ['reinforcementTuningSpec', 'hyperParameters', 'checkpointInterval'],
      fromCheckpointInterval,
    );
  }

  const fromMaxOutputTokens = common.getValueByPath(fromObject, [
    'maxOutputTokens',
  ]);
  if (parentObject !== undefined && fromMaxOutputTokens != null) {
    common.setValueByPath(
      parentObject,
      ['reinforcementTuningSpec', 'hyperParameters', 'maxOutputTokens'],
      fromMaxOutputTokens,
    );
  }

  const fromThinkingLevel = common.getValueByPath(fromObject, [
    'thinkingLevel',
  ]);
  if (parentObject !== undefined && fromThinkingLevel != null) {
    common.setValueByPath(
      parentObject,
      ['reinforcementTuningSpec', 'hyperParameters', 'thinkingLevel'],
      fromThinkingLevel,
    );
  }

  const fromValidationDatasetUri = common.getValueByPath(fromObject, [
    'validationDatasetUri',
  ]);
  if (parentObject !== undefined && fromValidationDatasetUri != null) {
    common.setValueByPath(
      parentObject,
      ['reinforcementTuningSpec', 'validationDatasetUri'],
      fromValidationDatasetUri,
    );
  }

  const fromEncryptionSpec = common.getValueByPath(fromObject, [
    'encryptionSpec',
  ]);
  if (parentObject !== undefined && fromEncryptionSpec != null) {
    common.setValueByPath(parentObject, ['encryptionSpec'], fromEncryptionSpec);
  }

  return toObject;
}

export function createTuningJobParametersPrivateToMldev(
  fromObject: types.CreateTuningJobParametersPrivate,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromBaseModel = common.getValueByPath(fromObject, ['baseModel']);
  if (fromBaseModel != null) {
    common.setValueByPath(toObject, ['baseModel'], fromBaseModel);
  }

  const fromPreTunedModel = common.getValueByPath(fromObject, [
    'preTunedModel',
  ]);
  if (fromPreTunedModel != null) {
    common.setValueByPath(toObject, ['preTunedModel'], fromPreTunedModel);
  }

  const fromTrainingDataset = common.getValueByPath(fromObject, [
    'trainingDataset',
  ]);
  if (fromTrainingDataset != null) {
    tuningDatasetToMldev(fromTrainingDataset, rootObject);
  }

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    createTuningJobConfigToMldev(fromConfig, toObject, rootObject);
  }

  return toObject;
}

export function createTuningJobParametersPrivateToVertex(
  fromObject: types.CreateTuningJobParametersPrivate,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromBaseModel = common.getValueByPath(fromObject, ['baseModel']);
  if (fromBaseModel != null) {
    common.setValueByPath(toObject, ['baseModel'], fromBaseModel);
  }

  const fromPreTunedModel = common.getValueByPath(fromObject, [
    'preTunedModel',
  ]);
  if (fromPreTunedModel != null) {
    common.setValueByPath(toObject, ['preTunedModel'], fromPreTunedModel);
  }

  const fromTrainingDataset = common.getValueByPath(fromObject, [
    'trainingDataset',
  ]);
  if (fromTrainingDataset != null) {
    tuningDatasetToVertex(fromTrainingDataset, toObject, rootObject);
  }

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    createTuningJobConfigToVertex(fromConfig, toObject, rootObject);
  }

  return toObject;
}

export function distillationHyperParametersFromVertex(
  fromObject: types.DistillationHyperParameters,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromEpochCount = common.getValueByPath(fromObject, ['epochCount']);
  if (fromEpochCount != null) {
    common.setValueByPath(toObject, ['epochCount'], fromEpochCount);
  }

  const fromLearningRateMultiplier = common.getValueByPath(fromObject, [
    'learningRateMultiplier',
  ]);
  if (fromLearningRateMultiplier != null) {
    common.setValueByPath(
      toObject,
      ['learningRateMultiplier'],
      fromLearningRateMultiplier,
    );
  }

  const fromAdapterSize = common.getValueByPath(fromObject, ['adapterSize']);
  if (fromAdapterSize != null) {
    common.setValueByPath(toObject, ['adapterSize'], fromAdapterSize);
  }

  const fromBatchSize = common.getValueByPath(fromObject, ['batchSize']);
  if (fromBatchSize != null) {
    common.setValueByPath(toObject, ['batchSize'], fromBatchSize);
  }

  const fromLearningRate = common.getValueByPath(fromObject, ['learningRate']);
  if (fromLearningRate != null) {
    common.setValueByPath(toObject, ['learningRate'], fromLearningRate);
  }

  const fromGenerationConfig = common.getValueByPath(fromObject, [
    'generationConfig',
  ]);
  if (fromGenerationConfig != null) {
    common.setValueByPath(
      toObject,
      ['generationConfig'],
      generationConfigFromVertex(fromGenerationConfig, rootObject),
    );
  }

  return toObject;
}

export function distillationSamplingSpecFromVertex(
  fromObject: types.DistillationSamplingSpec,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromPromptDatasetUri = common.getValueByPath(fromObject, [
    'promptDatasetUri',
  ]);
  if (fromPromptDatasetUri != null) {
    common.setValueByPath(toObject, ['promptDatasetUri'], fromPromptDatasetUri);
  }

  const fromValidationDatasetUri = common.getValueByPath(fromObject, [
    'validationDatasetUri',
  ]);
  if (fromValidationDatasetUri != null) {
    common.setValueByPath(
      toObject,
      ['validationDatasetUri'],
      fromValidationDatasetUri,
    );
  }

  const fromBaseTeacherModel = common.getValueByPath(fromObject, [
    'baseTeacherModel',
  ]);
  if (fromBaseTeacherModel != null) {
    common.setValueByPath(toObject, ['baseTeacherModel'], fromBaseTeacherModel);
  }

  const fromTunedTeacherModelSource = common.getValueByPath(fromObject, [
    'tunedTeacherModelSource',
  ]);
  if (fromTunedTeacherModelSource != null) {
    common.setValueByPath(
      toObject,
      ['tunedTeacherModelSource'],
      fromTunedTeacherModelSource,
    );
  }

  const fromHyperparameters = common.getValueByPath(fromObject, [
    'hyperparameters',
  ]);
  if (fromHyperparameters != null) {
    common.setValueByPath(
      toObject,
      ['hyperparameters'],
      distillationHyperParametersFromVertex(fromHyperparameters, rootObject),
    );
  }

  return toObject;
}

export function distillationSpecFromVertex(
  fromObject: types.DistillationSpec,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromBaseTeacherModel = common.getValueByPath(fromObject, [
    'baseTeacherModel',
  ]);
  if (fromBaseTeacherModel != null) {
    common.setValueByPath(toObject, ['baseTeacherModel'], fromBaseTeacherModel);
  }

  const fromHyperParameters = common.getValueByPath(fromObject, [
    'hyperParameters',
  ]);
  if (fromHyperParameters != null) {
    common.setValueByPath(
      toObject,
      ['hyperParameters'],
      distillationHyperParametersFromVertex(fromHyperParameters, rootObject),
    );
  }

  const fromPipelineRootDirectory = common.getValueByPath(fromObject, [
    'pipelineRootDirectory',
  ]);
  if (fromPipelineRootDirectory != null) {
    common.setValueByPath(
      toObject,
      ['pipelineRootDirectory'],
      fromPipelineRootDirectory,
    );
  }

  const fromPromptDatasetUri = common.getValueByPath(fromObject, [
    'promptDatasetUri',
  ]);
  if (fromPromptDatasetUri != null) {
    common.setValueByPath(toObject, ['promptDatasetUri'], fromPromptDatasetUri);
  }

  const fromStudentModel = common.getValueByPath(fromObject, ['studentModel']);
  if (fromStudentModel != null) {
    common.setValueByPath(toObject, ['studentModel'], fromStudentModel);
  }

  const fromTrainingDatasetUri = common.getValueByPath(fromObject, [
    'trainingDatasetUri',
  ]);
  if (fromTrainingDatasetUri != null) {
    common.setValueByPath(
      toObject,
      ['trainingDatasetUri'],
      fromTrainingDatasetUri,
    );
  }

  const fromTunedTeacherModelSource = common.getValueByPath(fromObject, [
    'tunedTeacherModelSource',
  ]);
  if (fromTunedTeacherModelSource != null) {
    common.setValueByPath(
      toObject,
      ['tunedTeacherModelSource'],
      fromTunedTeacherModelSource,
    );
  }

  const fromTuningMode = common.getValueByPath(fromObject, ['tuningMode']);
  if (fromTuningMode != null) {
    common.setValueByPath(toObject, ['tuningMode'], fromTuningMode);
  }

  const fromValidationDatasetUri = common.getValueByPath(fromObject, [
    'validationDatasetUri',
  ]);
  if (fromValidationDatasetUri != null) {
    common.setValueByPath(
      toObject,
      ['validationDatasetUri'],
      fromValidationDatasetUri,
    );
  }

  return toObject;
}

export function executableCodeToVertex(
  fromObject: types.ExecutableCode,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromCode = common.getValueByPath(fromObject, ['code']);
  if (fromCode != null) {
    common.setValueByPath(toObject, ['code'], fromCode);
  }

  const fromLanguage = common.getValueByPath(fromObject, ['language']);
  if (fromLanguage != null) {
    common.setValueByPath(toObject, ['language'], fromLanguage);
  }

  if (common.getValueByPath(fromObject, ['id']) !== undefined) {
    throw new Error(
      'id parameter is only supported in Gemini Developer API mode, not in Gemini Enterprise Agent Platform mode.',
    );
  }

  return toObject;
}

export function generationConfigFromVertex(
  fromObject: types.GenerationConfig,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromModelSelectionConfig = common.getValueByPath(fromObject, [
    'modelConfig',
  ]);
  if (fromModelSelectionConfig != null) {
    common.setValueByPath(
      toObject,
      ['modelSelectionConfig'],
      fromModelSelectionConfig,
    );
  }

  const fromResponseJsonSchema = common.getValueByPath(fromObject, [
    'responseJsonSchema',
  ]);
  if (fromResponseJsonSchema != null) {
    common.setValueByPath(
      toObject,
      ['responseJsonSchema'],
      t.tJsonSchema(fromResponseJsonSchema),
    );
  }

  const fromAudioTranscriptionConfig = common.getValueByPath(fromObject, [
    'audioTranscriptionConfig',
  ]);
  if (fromAudioTranscriptionConfig != null) {
    common.setValueByPath(
      toObject,
      ['audioTranscriptionConfig'],
      fromAudioTranscriptionConfig,
    );
  }

  const fromAudioTimestamp = common.getValueByPath(fromObject, [
    'audioTimestamp',
  ]);
  if (fromAudioTimestamp != null) {
    common.setValueByPath(toObject, ['audioTimestamp'], fromAudioTimestamp);
  }

  const fromCandidateCount = common.getValueByPath(fromObject, [
    'candidateCount',
  ]);
  if (fromCandidateCount != null) {
    common.setValueByPath(toObject, ['candidateCount'], fromCandidateCount);
  }

  const fromEnableAffectiveDialog = common.getValueByPath(fromObject, [
    'enableAffectiveDialog',
  ]);
  if (fromEnableAffectiveDialog != null) {
    common.setValueByPath(
      toObject,
      ['enableAffectiveDialog'],
      fromEnableAffectiveDialog,
    );
  }

  const fromFrequencyPenalty = common.getValueByPath(fromObject, [
    'frequencyPenalty',
  ]);
  if (fromFrequencyPenalty != null) {
    common.setValueByPath(toObject, ['frequencyPenalty'], fromFrequencyPenalty);
  }

  const fromLogprobs = common.getValueByPath(fromObject, ['logprobs']);
  if (fromLogprobs != null) {
    common.setValueByPath(toObject, ['logprobs'], fromLogprobs);
  }

  const fromMaxOutputTokens = common.getValueByPath(fromObject, [
    'maxOutputTokens',
  ]);
  if (fromMaxOutputTokens != null) {
    common.setValueByPath(toObject, ['maxOutputTokens'], fromMaxOutputTokens);
  }

  const fromMediaResolution = common.getValueByPath(fromObject, [
    'mediaResolution',
  ]);
  if (fromMediaResolution != null) {
    common.setValueByPath(toObject, ['mediaResolution'], fromMediaResolution);
  }

  const fromPresencePenalty = common.getValueByPath(fromObject, [
    'presencePenalty',
  ]);
  if (fromPresencePenalty != null) {
    common.setValueByPath(toObject, ['presencePenalty'], fromPresencePenalty);
  }

  const fromResponseFormat = common.getValueByPath(fromObject, [
    'responseFormat',
  ]);
  if (fromResponseFormat != null) {
    let transformedList = fromResponseFormat;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return item;
      });
    }
    common.setValueByPath(toObject, ['responseFormat'], transformedList);
  }

  const fromResponseLogprobs = common.getValueByPath(fromObject, [
    'responseLogprobs',
  ]);
  if (fromResponseLogprobs != null) {
    common.setValueByPath(toObject, ['responseLogprobs'], fromResponseLogprobs);
  }

  const fromResponseMimeType = common.getValueByPath(fromObject, [
    'responseMimeType',
  ]);
  if (fromResponseMimeType != null) {
    common.setValueByPath(toObject, ['responseMimeType'], fromResponseMimeType);
  }

  const fromResponseModalities = common.getValueByPath(fromObject, [
    'responseModalities',
  ]);
  if (fromResponseModalities != null) {
    common.setValueByPath(
      toObject,
      ['responseModalities'],
      fromResponseModalities,
    );
  }

  const fromResponseSchema = common.getValueByPath(fromObject, [
    'responseSchema',
  ]);
  if (fromResponseSchema != null) {
    common.setValueByPath(toObject, ['responseSchema'], fromResponseSchema);
  }

  const fromRoutingConfig = common.getValueByPath(fromObject, [
    'routingConfig',
  ]);
  if (fromRoutingConfig != null) {
    common.setValueByPath(toObject, ['routingConfig'], fromRoutingConfig);
  }

  const fromSeed = common.getValueByPath(fromObject, ['seed']);
  if (fromSeed != null) {
    common.setValueByPath(toObject, ['seed'], fromSeed);
  }

  const fromSpeechConfig = common.getValueByPath(fromObject, ['speechConfig']);
  if (fromSpeechConfig != null) {
    common.setValueByPath(toObject, ['speechConfig'], fromSpeechConfig);
  }

  const fromStopSequences = common.getValueByPath(fromObject, [
    'stopSequences',
  ]);
  if (fromStopSequences != null) {
    common.setValueByPath(toObject, ['stopSequences'], fromStopSequences);
  }

  const fromTemperature = common.getValueByPath(fromObject, ['temperature']);
  if (fromTemperature != null) {
    common.setValueByPath(toObject, ['temperature'], fromTemperature);
  }

  const fromThinkingConfig = common.getValueByPath(fromObject, [
    'thinkingConfig',
  ]);
  if (fromThinkingConfig != null) {
    common.setValueByPath(toObject, ['thinkingConfig'], fromThinkingConfig);
  }

  const fromTopK = common.getValueByPath(fromObject, ['topK']);
  if (fromTopK != null) {
    common.setValueByPath(toObject, ['topK'], fromTopK);
  }

  const fromTopP = common.getValueByPath(fromObject, ['topP']);
  if (fromTopP != null) {
    common.setValueByPath(toObject, ['topP'], fromTopP);
  }

  return toObject;
}

export function getTuningJobParametersToMldev(
  fromObject: types.GetTuningJobParameters,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['_url', 'name'], fromName);
  }

  return toObject;
}

export function getTuningJobParametersToVertex(
  fromObject: types.GetTuningJobParameters,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['_url', 'name'], fromName);
  }

  return toObject;
}

export function listTuningJobsConfigToVertex(
  fromObject: types.ListTuningJobsConfig,
  parentObject: Record<string, unknown>,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromPageSize = common.getValueByPath(fromObject, ['pageSize']);
  if (parentObject !== undefined && fromPageSize != null) {
    common.setValueByPath(parentObject, ['_query', 'pageSize'], fromPageSize);
  }

  const fromPageToken = common.getValueByPath(fromObject, ['pageToken']);
  if (parentObject !== undefined && fromPageToken != null) {
    common.setValueByPath(parentObject, ['_query', 'pageToken'], fromPageToken);
  }

  const fromFilter = common.getValueByPath(fromObject, ['filter']);
  if (parentObject !== undefined && fromFilter != null) {
    common.setValueByPath(parentObject, ['_query', 'filter'], fromFilter);
  }

  return toObject;
}

export function listTuningJobsParametersToVertex(
  fromObject: types.ListTuningJobsParameters,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    listTuningJobsConfigToVertex(fromConfig, toObject, rootObject);
  }

  return toObject;
}

export function listTuningJobsResponseFromVertex(
  fromObject: types.ListTuningJobsResponse,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  const fromNextPageToken = common.getValueByPath(fromObject, [
    'nextPageToken',
  ]);
  if (fromNextPageToken != null) {
    common.setValueByPath(toObject, ['nextPageToken'], fromNextPageToken);
  }

  const fromTuningJobs = common.getValueByPath(fromObject, ['tuningJobs']);
  if (fromTuningJobs != null) {
    let transformedList = fromTuningJobs;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return tuningJobFromVertex(item, rootObject);
      });
    }
    common.setValueByPath(toObject, ['tuningJobs'], transformedList);
  }

  return toObject;
}

export function partToVertex(
  fromObject: types.Part,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromMediaResolution = common.getValueByPath(fromObject, [
    'mediaResolution',
  ]);
  if (fromMediaResolution != null) {
    common.setValueByPath(toObject, ['mediaResolution'], fromMediaResolution);
  }

  if (common.getValueByPath(fromObject, ['toolCall']) !== undefined) {
    throw new Error(
      'toolCall parameter is only supported in Gemini Developer API mode, not in Gemini Enterprise Agent Platform mode.',
    );
  }

  if (common.getValueByPath(fromObject, ['toolResponse']) !== undefined) {
    throw new Error(
      'toolResponse parameter is only supported in Gemini Developer API mode, not in Gemini Enterprise Agent Platform mode.',
    );
  }

  const fromAudioTranscription = common.getValueByPath(fromObject, [
    'audioTranscription',
  ]);
  if (fromAudioTranscription != null) {
    common.setValueByPath(
      toObject,
      ['audioTranscription'],
      fromAudioTranscription,
    );
  }

  const fromCodeExecutionResult = common.getValueByPath(fromObject, [
    'codeExecutionResult',
  ]);
  if (fromCodeExecutionResult != null) {
    common.setValueByPath(
      toObject,
      ['codeExecutionResult'],
      codeExecutionResultToVertex(fromCodeExecutionResult, rootObject),
    );
  }

  const fromExecutableCode = common.getValueByPath(fromObject, [
    'executableCode',
  ]);
  if (fromExecutableCode != null) {
    common.setValueByPath(
      toObject,
      ['executableCode'],
      executableCodeToVertex(fromExecutableCode, rootObject),
    );
  }

  const fromFileData = common.getValueByPath(fromObject, ['fileData']);
  if (fromFileData != null) {
    common.setValueByPath(toObject, ['fileData'], fromFileData);
  }

  const fromFunctionCall = common.getValueByPath(fromObject, ['functionCall']);
  if (fromFunctionCall != null) {
    common.setValueByPath(toObject, ['functionCall'], fromFunctionCall);
  }

  const fromFunctionResponse = common.getValueByPath(fromObject, [
    'functionResponse',
  ]);
  if (fromFunctionResponse != null) {
    common.setValueByPath(toObject, ['functionResponse'], fromFunctionResponse);
  }

  const fromInlineData = common.getValueByPath(fromObject, ['inlineData']);
  if (fromInlineData != null) {
    common.setValueByPath(toObject, ['inlineData'], fromInlineData);
  }

  const fromText = common.getValueByPath(fromObject, ['text']);
  if (fromText != null) {
    common.setValueByPath(toObject, ['text'], fromText);
  }

  const fromThought = common.getValueByPath(fromObject, ['thought']);
  if (fromThought != null) {
    common.setValueByPath(toObject, ['thought'], fromThought);
  }

  const fromThoughtSignature = common.getValueByPath(fromObject, [
    'thoughtSignature',
  ]);
  if (fromThoughtSignature != null) {
    common.setValueByPath(toObject, ['thoughtSignature'], fromThoughtSignature);
  }

  const fromVideoMetadata = common.getValueByPath(fromObject, [
    'videoMetadata',
  ]);
  if (fromVideoMetadata != null) {
    common.setValueByPath(toObject, ['videoMetadata'], fromVideoMetadata);
  }

  if (common.getValueByPath(fromObject, ['partMetadata']) !== undefined) {
    throw new Error(
      'partMetadata parameter is only supported in Gemini Developer API mode, not in Gemini Enterprise Agent Platform mode.',
    );
  }

  return toObject;
}

export function reinforcementTuningExampleToVertex(
  fromObject: types.ReinforcementTuningExample,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromReferences = common.getValueByPath(fromObject, ['references']);
  if (fromReferences != null) {
    common.setValueByPath(toObject, ['references'], fromReferences);
  }

  const fromContents = common.getValueByPath(fromObject, ['contents']);
  if (fromContents != null) {
    let transformedList = fromContents;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return contentToVertex(item, rootObject);
      });
    }
    common.setValueByPath(toObject, ['contents'], transformedList);
  }

  const fromSystemInstruction = common.getValueByPath(fromObject, [
    'systemInstruction',
  ]);
  if (fromSystemInstruction != null) {
    common.setValueByPath(
      toObject,
      ['systemInstruction'],
      contentToVertex(fromSystemInstruction, rootObject),
    );
  }

  return toObject;
}

export function tunedModelFromMldev(
  fromObject: types.TunedModel,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromModel = common.getValueByPath(fromObject, ['name']);
  if (fromModel != null) {
    common.setValueByPath(toObject, ['model'], fromModel);
  }

  const fromEndpoint = common.getValueByPath(fromObject, ['name']);
  if (fromEndpoint != null) {
    common.setValueByPath(toObject, ['endpoint'], fromEndpoint);
  }

  return toObject;
}

export function tuningDatasetToMldev(
  fromObject: types.TuningDataset,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  if (common.getValueByPath(fromObject, ['gcsUri']) !== undefined) {
    throw new Error(
      'gcsUri parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  if (
    common.getValueByPath(fromObject, ['vertexDatasetResource']) !== undefined
  ) {
    throw new Error(
      'vertexDatasetResource parameter is only supported in Gemini Enterprise Agent Platform mode, not in Gemini Developer API mode.',
    );
  }

  const fromExamples = common.getValueByPath(fromObject, ['examples']);
  if (fromExamples != null) {
    let transformedList = fromExamples;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return item;
      });
    }
    common.setValueByPath(toObject, ['examples', 'examples'], transformedList);
  }

  return toObject;
}

export function tuningDatasetToVertex(
  fromObject: types.TuningDataset,
  parentObject: Record<string, unknown>,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  let discriminatorGcsUri = common.getValueByPath(rootObject, [
    'config',
    'method',
  ]);
  if (discriminatorGcsUri === undefined) {
    discriminatorGcsUri = 'SUPERVISED_FINE_TUNING';
  }
  if (discriminatorGcsUri === 'SUPERVISED_FINE_TUNING') {
    const fromGcsUri = common.getValueByPath(fromObject, ['gcsUri']);
    if (parentObject !== undefined && fromGcsUri != null) {
      common.setValueByPath(
        parentObject,
        ['supervisedTuningSpec', 'trainingDatasetUri'],
        fromGcsUri,
      );
    }
  } else if (discriminatorGcsUri === 'PREFERENCE_TUNING') {
    const fromGcsUri = common.getValueByPath(fromObject, ['gcsUri']);
    if (parentObject !== undefined && fromGcsUri != null) {
      common.setValueByPath(
        parentObject,
        ['preferenceOptimizationSpec', 'trainingDatasetUri'],
        fromGcsUri,
      );
    }
  } else if (discriminatorGcsUri === 'DISTILLATION') {
    const fromGcsUri = common.getValueByPath(fromObject, ['gcsUri']);
    if (parentObject !== undefined && fromGcsUri != null) {
      common.setValueByPath(
        parentObject,
        ['distillationSpec', 'promptDatasetUri'],
        fromGcsUri,
      );
    }
  } else if (discriminatorGcsUri === 'REINFORCEMENT_TUNING') {
    const fromGcsUri = common.getValueByPath(fromObject, ['gcsUri']);
    if (parentObject !== undefined && fromGcsUri != null) {
      common.setValueByPath(
        parentObject,
        ['reinforcementTuningSpec', 'trainingDatasetUri'],
        fromGcsUri,
      );
    }
  }

  let discriminatorVertexDatasetResource = common.getValueByPath(rootObject, [
    'config',
    'method',
  ]);
  if (discriminatorVertexDatasetResource === undefined) {
    discriminatorVertexDatasetResource = 'SUPERVISED_FINE_TUNING';
  }
  if (discriminatorVertexDatasetResource === 'SUPERVISED_FINE_TUNING') {
    const fromVertexDatasetResource = common.getValueByPath(fromObject, [
      'vertexDatasetResource',
    ]);
    if (parentObject !== undefined && fromVertexDatasetResource != null) {
      common.setValueByPath(
        parentObject,
        ['supervisedTuningSpec', 'trainingDatasetUri'],
        fromVertexDatasetResource,
      );
    }
  } else if (discriminatorVertexDatasetResource === 'PREFERENCE_TUNING') {
    const fromVertexDatasetResource = common.getValueByPath(fromObject, [
      'vertexDatasetResource',
    ]);
    if (parentObject !== undefined && fromVertexDatasetResource != null) {
      common.setValueByPath(
        parentObject,
        ['preferenceOptimizationSpec', 'trainingDatasetUri'],
        fromVertexDatasetResource,
      );
    }
  } else if (discriminatorVertexDatasetResource === 'DISTILLATION') {
    const fromVertexDatasetResource = common.getValueByPath(fromObject, [
      'vertexDatasetResource',
    ]);
    if (parentObject !== undefined && fromVertexDatasetResource != null) {
      common.setValueByPath(
        parentObject,
        ['distillationSpec', 'promptDatasetUri'],
        fromVertexDatasetResource,
      );
    }
  } else if (discriminatorVertexDatasetResource === 'REINFORCEMENT_TUNING') {
    const fromVertexDatasetResource = common.getValueByPath(fromObject, [
      'vertexDatasetResource',
    ]);
    if (parentObject !== undefined && fromVertexDatasetResource != null) {
      common.setValueByPath(
        parentObject,
        ['reinforcementTuningSpec', 'trainingDatasetUri'],
        fromVertexDatasetResource,
      );
    }
  }
  if (common.getValueByPath(fromObject, ['examples']) !== undefined) {
    throw new Error(
      'examples parameter is only supported in Gemini Developer API mode, not in Gemini Enterprise Agent Platform mode.',
    );
  }

  return toObject;
}

export function tuningJobFromMldev(
  fromObject: types.TuningJob,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['name'], fromName);
  }

  const fromState = common.getValueByPath(fromObject, ['state']);
  if (fromState != null) {
    common.setValueByPath(toObject, ['state'], t.tTuningJobStatus(fromState));
  }

  const fromCreateTime = common.getValueByPath(fromObject, ['createTime']);
  if (fromCreateTime != null) {
    common.setValueByPath(toObject, ['createTime'], fromCreateTime);
  }

  const fromStartTime = common.getValueByPath(fromObject, [
    'tuningTask',
    'startTime',
  ]);
  if (fromStartTime != null) {
    common.setValueByPath(toObject, ['startTime'], fromStartTime);
  }

  const fromEndTime = common.getValueByPath(fromObject, [
    'tuningTask',
    'completeTime',
  ]);
  if (fromEndTime != null) {
    common.setValueByPath(toObject, ['endTime'], fromEndTime);
  }

  const fromUpdateTime = common.getValueByPath(fromObject, ['updateTime']);
  if (fromUpdateTime != null) {
    common.setValueByPath(toObject, ['updateTime'], fromUpdateTime);
  }

  const fromDescription = common.getValueByPath(fromObject, ['description']);
  if (fromDescription != null) {
    common.setValueByPath(toObject, ['description'], fromDescription);
  }

  const fromBaseModel = common.getValueByPath(fromObject, ['baseModel']);
  if (fromBaseModel != null) {
    common.setValueByPath(toObject, ['baseModel'], fromBaseModel);
  }

  const fromTunedModel = common.getValueByPath(fromObject, ['_self']);
  if (fromTunedModel != null) {
    common.setValueByPath(
      toObject,
      ['tunedModel'],
      tunedModelFromMldev(fromTunedModel, rootObject),
    );
  }

  return toObject;
}

export function tuningJobFromVertex(
  fromObject: types.TuningJob,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['name'], fromName);
  }

  const fromState = common.getValueByPath(fromObject, ['state']);
  if (fromState != null) {
    common.setValueByPath(toObject, ['state'], t.tTuningJobStatus(fromState));
  }

  const fromCreateTime = common.getValueByPath(fromObject, ['createTime']);
  if (fromCreateTime != null) {
    common.setValueByPath(toObject, ['createTime'], fromCreateTime);
  }

  const fromStartTime = common.getValueByPath(fromObject, ['startTime']);
  if (fromStartTime != null) {
    common.setValueByPath(toObject, ['startTime'], fromStartTime);
  }

  const fromEndTime = common.getValueByPath(fromObject, ['endTime']);
  if (fromEndTime != null) {
    common.setValueByPath(toObject, ['endTime'], fromEndTime);
  }

  const fromUpdateTime = common.getValueByPath(fromObject, ['updateTime']);
  if (fromUpdateTime != null) {
    common.setValueByPath(toObject, ['updateTime'], fromUpdateTime);
  }

  const fromError = common.getValueByPath(fromObject, ['error']);
  if (fromError != null) {
    common.setValueByPath(toObject, ['error'], fromError);
  }

  const fromDescription = common.getValueByPath(fromObject, ['description']);
  if (fromDescription != null) {
    common.setValueByPath(toObject, ['description'], fromDescription);
  }

  const fromBaseModel = common.getValueByPath(fromObject, ['baseModel']);
  if (fromBaseModel != null) {
    common.setValueByPath(toObject, ['baseModel'], fromBaseModel);
  }

  const fromTunedModel = common.getValueByPath(fromObject, ['tunedModel']);
  if (fromTunedModel != null) {
    common.setValueByPath(toObject, ['tunedModel'], fromTunedModel);
  }

  const fromPreTunedModel = common.getValueByPath(fromObject, [
    'preTunedModel',
  ]);
  if (fromPreTunedModel != null) {
    common.setValueByPath(toObject, ['preTunedModel'], fromPreTunedModel);
  }

  const fromSupervisedTuningSpec = common.getValueByPath(fromObject, [
    'supervisedTuningSpec',
  ]);
  if (fromSupervisedTuningSpec != null) {
    common.setValueByPath(
      toObject,
      ['supervisedTuningSpec'],
      fromSupervisedTuningSpec,
    );
  }

  const fromPreferenceOptimizationSpec = common.getValueByPath(fromObject, [
    'preferenceOptimizationSpec',
  ]);
  if (fromPreferenceOptimizationSpec != null) {
    common.setValueByPath(
      toObject,
      ['preferenceOptimizationSpec'],
      fromPreferenceOptimizationSpec,
    );
  }

  const fromDistillationSamplingSpec = common.getValueByPath(fromObject, [
    'distillationSamplingSpec',
  ]);
  if (fromDistillationSamplingSpec != null) {
    common.setValueByPath(
      toObject,
      ['distillationSamplingSpec'],
      distillationSamplingSpecFromVertex(
        fromDistillationSamplingSpec,
        rootObject,
      ),
    );
  }

  const fromDistillationSpec = common.getValueByPath(fromObject, [
    'distillationSpec',
  ]);
  if (fromDistillationSpec != null) {
    common.setValueByPath(
      toObject,
      ['distillationSpec'],
      distillationSpecFromVertex(fromDistillationSpec, rootObject),
    );
  }

  const fromReinforcementTuningSpec = common.getValueByPath(fromObject, [
    'reinforcementTuningSpec',
  ]);
  if (fromReinforcementTuningSpec != null) {
    common.setValueByPath(
      toObject,
      ['reinforcementTuningSpec'],
      fromReinforcementTuningSpec,
    );
  }

  const fromTuningDataStats = common.getValueByPath(fromObject, [
    'tuningDataStats',
  ]);
  if (fromTuningDataStats != null) {
    common.setValueByPath(toObject, ['tuningDataStats'], fromTuningDataStats);
  }

  const fromEncryptionSpec = common.getValueByPath(fromObject, [
    'encryptionSpec',
  ]);
  if (fromEncryptionSpec != null) {
    common.setValueByPath(toObject, ['encryptionSpec'], fromEncryptionSpec);
  }

  const fromPartnerModelTuningSpec = common.getValueByPath(fromObject, [
    'partnerModelTuningSpec',
  ]);
  if (fromPartnerModelTuningSpec != null) {
    common.setValueByPath(
      toObject,
      ['partnerModelTuningSpec'],
      fromPartnerModelTuningSpec,
    );
  }

  const fromCustomBaseModel = common.getValueByPath(fromObject, [
    'customBaseModel',
  ]);
  if (fromCustomBaseModel != null) {
    common.setValueByPath(toObject, ['customBaseModel'], fromCustomBaseModel);
  }

  const fromEvaluateDatasetRuns = common.getValueByPath(fromObject, [
    'evaluateDatasetRuns',
  ]);
  if (fromEvaluateDatasetRuns != null) {
    let transformedList = fromEvaluateDatasetRuns;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return item;
      });
    }
    common.setValueByPath(toObject, ['evaluateDatasetRuns'], transformedList);
  }

  const fromExperiment = common.getValueByPath(fromObject, ['experiment']);
  if (fromExperiment != null) {
    common.setValueByPath(toObject, ['experiment'], fromExperiment);
  }

  const fromFullFineTuningSpec = common.getValueByPath(fromObject, [
    'fullFineTuningSpec',
  ]);
  if (fromFullFineTuningSpec != null) {
    common.setValueByPath(
      toObject,
      ['fullFineTuningSpec'],
      fromFullFineTuningSpec,
    );
  }

  const fromLabels = common.getValueByPath(fromObject, ['labels']);
  if (fromLabels != null) {
    common.setValueByPath(toObject, ['labels'], fromLabels);
  }

  const fromOutputUri = common.getValueByPath(fromObject, ['outputUri']);
  if (fromOutputUri != null) {
    common.setValueByPath(toObject, ['outputUri'], fromOutputUri);
  }

  const fromPipelineJob = common.getValueByPath(fromObject, ['pipelineJob']);
  if (fromPipelineJob != null) {
    common.setValueByPath(toObject, ['pipelineJob'], fromPipelineJob);
  }

  const fromServiceAccount = common.getValueByPath(fromObject, [
    'serviceAccount',
  ]);
  if (fromServiceAccount != null) {
    common.setValueByPath(toObject, ['serviceAccount'], fromServiceAccount);
  }

  const fromTunedModelDisplayName = common.getValueByPath(fromObject, [
    'tunedModelDisplayName',
  ]);
  if (fromTunedModelDisplayName != null) {
    common.setValueByPath(
      toObject,
      ['tunedModelDisplayName'],
      fromTunedModelDisplayName,
    );
  }

  const fromTuningJobMetadata = common.getValueByPath(fromObject, [
    'tuningJobMetadata',
  ]);
  if (fromTuningJobMetadata != null) {
    common.setValueByPath(
      toObject,
      ['tuningJobMetadata'],
      fromTuningJobMetadata,
    );
  }

  const fromTuningJobState = common.getValueByPath(fromObject, [
    'tuningJobState',
  ]);
  if (fromTuningJobState != null) {
    common.setValueByPath(toObject, ['tuningJobState'], fromTuningJobState);
  }

  const fromVeoLoraTuningSpec = common.getValueByPath(fromObject, [
    'veoLoraTuningSpec',
  ]);
  if (fromVeoLoraTuningSpec != null) {
    common.setValueByPath(
      toObject,
      ['veoLoraTuningSpec'],
      fromVeoLoraTuningSpec,
    );
  }

  const fromVeoTuningSpec = common.getValueByPath(fromObject, [
    'veoTuningSpec',
  ]);
  if (fromVeoTuningSpec != null) {
    common.setValueByPath(toObject, ['veoTuningSpec'], fromVeoTuningSpec);
  }

  return toObject;
}

export function tuningOperationFromMldev(
  fromObject: types.TuningOperation,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['name'], fromName);
  }

  const fromMetadata = common.getValueByPath(fromObject, ['metadata']);
  if (fromMetadata != null) {
    common.setValueByPath(toObject, ['metadata'], fromMetadata);
  }

  const fromDone = common.getValueByPath(fromObject, ['done']);
  if (fromDone != null) {
    common.setValueByPath(toObject, ['done'], fromDone);
  }

  const fromError = common.getValueByPath(fromObject, ['error']);
  if (fromError != null) {
    common.setValueByPath(toObject, ['error'], fromError);
  }

  return toObject;
}

export function tuningValidationDatasetToVertex(
  fromObject: types.TuningValidationDataset,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromGcsUri = common.getValueByPath(fromObject, ['gcsUri']);
  if (fromGcsUri != null) {
    common.setValueByPath(toObject, ['validationDatasetUri'], fromGcsUri);
  }

  const fromVertexDatasetResource = common.getValueByPath(fromObject, [
    'vertexDatasetResource',
  ]);
  if (fromVertexDatasetResource != null) {
    common.setValueByPath(
      toObject,
      ['validationDatasetUri'],
      fromVertexDatasetResource,
    );
  }

  return toObject;
}

export function validateRewardParametersToVertex(
  fromObject: types.ValidateRewardParameters,
  rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromParent = common.getValueByPath(fromObject, ['parent']);
  if (fromParent != null) {
    common.setValueByPath(toObject, ['_url', 'parent'], fromParent);
  }

  const fromSampleResponse = common.getValueByPath(fromObject, [
    'sampleResponse',
  ]);
  if (fromSampleResponse != null) {
    common.setValueByPath(
      toObject,
      ['sampleResponse'],
      contentToVertex(fromSampleResponse, rootObject),
    );
  }

  const fromExample = common.getValueByPath(fromObject, ['example']);
  if (fromExample != null) {
    common.setValueByPath(
      toObject,
      ['example'],
      reinforcementTuningExampleToVertex(fromExample, rootObject),
    );
  }

  const fromSingleRewardConfig = common.getValueByPath(fromObject, [
    'singleRewardConfig',
  ]);
  if (fromSingleRewardConfig != null) {
    common.setValueByPath(
      toObject,
      ['singleRewardConfig'],
      fromSingleRewardConfig,
    );
  }

  const fromCompositeRewardConfig = common.getValueByPath(fromObject, [
    'compositeRewardConfig',
  ]);
  if (fromCompositeRewardConfig != null) {
    common.setValueByPath(
      toObject,
      ['compositeRewardConfig'],
      fromCompositeRewardConfig,
    );
  }

  return toObject;
}

export function validateRewardResponseFromVertex(
  fromObject: types.ValidateRewardResponse,
  _rootObject?: unknown,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  const fromOverallReward = common.getValueByPath(fromObject, [
    'overallReward',
  ]);
  if (fromOverallReward != null) {
    common.setValueByPath(toObject, ['overallReward'], fromOverallReward);
  }

  const fromError = common.getValueByPath(fromObject, ['error']);
  if (fromError != null) {
    common.setValueByPath(toObject, ['error'], fromError);
  }

  const fromRewardInfoDetails = common.getValueByPath(fromObject, [
    'rewardInfoDetails',
  ]);
  if (fromRewardInfoDetails != null) {
    common.setValueByPath(
      toObject,
      ['rewardInfoDetails'],
      fromRewardInfoDetails,
    );
  }

  return toObject;
}
